// «Evidencia inicial» de la card de observación (README §1): lo que nació
// con la observación —fotos de nacimiento y las palabras del propietario—
// en un bloque que NO crece con la cantidad de evidencia.
//
// - Miniaturas de 72×72, máximo 4. Si hay más, la 4.ª lleva el overlay
//   «+N» con N = restantes + 1 (la 4.ª más las que no se ven). Todas se
//   ven en el visor (`dialog.vlb` existente, circular con ← →).
// - Un solo párrafo con las descripciones concatenadas en orden. Con más
//   de una, cada tramo lleva su chip de origen («Nota i»: el wire no trae
//   la duración de la nota de voz). Sin expandir/colapsar (descartado).
// - Voz = SIEMPRE texto, jamás un <audio> (regla dura del handoff).

import { Fragment, useState } from "react";
import type { OwnerDescription } from "@bitacorapro/api-types";
import { Icon } from "./icons";
import { Lightbox } from "./Lightbox";

export const EVIDENCIA_MAX_THUMBS = 4;

export type EvidenciaFoto = { url: string; caption: string | null };

function plural(n: number, uno: string, varios: string): string {
  return `${n} ${n === 1 ? uno : varios}`;
}

export function EvidenciaInicial({
  fotos,
  descripciones,
  autor,
  rol,
  fotosEnConsulta = 0,
}: {
  fotos: readonly EvidenciaFoto[];
  descripciones: readonly OwnerDescription[];
  /** Quien la levantó (nombre del propietario), si se conoce. */
  autor: string | null;
  /** «propietario» / «inmobiliaria», según el origen. */
  rol: string;
  /** ADR-0163 — una observación que nació de una consulta no trae fotos de
   *  nacimiento: las del propietario cuelgan de «Desde la consulta». Sin
   *  esto la card decía «0 fotos · Sin fotos.» justo encima de ellas. */
  fotosEnConsulta?: number;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const shown = fotos.slice(0, EVIDENCIA_MAX_THUMBS);
  const resto = fotos.length - EVIDENCIA_MAX_THUMBS + 1;
  const varias = descripciones.length > 1;
  const meta = [
    autor,
    rol,
    fotos.length === 0 && fotosEnConsulta > 0
      ? `${plural(fotosEnConsulta, "foto", "fotos")} de la consulta`
      : plural(fotos.length, "foto", "fotos"),
    varias ? plural(descripciones.length, "nota", "notas") : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section className="obs-detail__ev" aria-labelledby="obs-ev-ini">
      <div className="obs-detail__ev-head">
        <span className="drawer__section-label" id="obs-ev-ini">
          Evidencia inicial
        </span>
        <span className="cell-meta">{meta}</span>
      </div>
      <div className="evc">
        {shown.length > 0 ? (
          <div className="evc__thumbs">
            {shown.map((f, i) => {
              const mas = i === EVIDENCIA_MAX_THUMBS - 1 && fotos.length > EVIDENCIA_MAX_THUMBS;
              return (
                <button
                  key={`${f.url}-${i}`}
                  type="button"
                  className="evc__thumb"
                  onClick={() => setOpen(i)}
                  aria-label={
                    mas
                      ? `Ver foto ${i + 1} de ${fotos.length} (${resto} más)`
                      : `Ver foto ${i + 1} de ${fotos.length}`
                  }
                >
                  <img src={f.url} alt="" />
                  {mas && (
                    <span className="evc__more" aria-hidden="true">
                      +{resto}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ) : fotosEnConsulta > 0 ? (
          <p className="cell-sub cell-sub--italic evc__none">
            Las fotos están en «Desde la consulta», más abajo.
          </p>
        ) : (
          <p className="cell-sub cell-sub--italic evc__none">Sin fotos.</p>
        )}
        <div className="evc__notes">
          {descripciones.length > 0 && (
            <p className="evc__desc">
              {descripciones.map((d, i) => (
                <Fragment key={i}>
                  {i > 0 && " "}
                  {varias && (
                    <span className="evc__src">
                      <Icon.Mic s={12} />
                      Nota {i + 1}
                    </span>
                  )}
                  {varias && " "}
                  {d.redacted || d.text === null ? (
                    <em>Texto retirado a petición del propietario</em>
                  ) : (
                    d.text
                  )}
                </Fragment>
              ))}
            </p>
          )}
        </div>
      </div>
      <Lightbox
        photos={fotos.map((f) => ({
          url: f.url,
          caption: f.caption,
          areaTag: `Evidencia inicial · ${rol}`,
        }))}
        openIndex={open}
        onClose={() => setOpen(null)}
        onNavigate={setOpen}
        ariaLabel="Evidencia inicial de la observación"
      />
    </section>
  );
}
